import {Future} from 'external/dev_april_corgi+/js/common/futures';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent} from 'external/dev_april_corgi+/js/corgi/events';
import {CHANGED} from 'external/dev_april_corgi+/js/emu/events';

import {GetCurrentUserResponse} from 'petri/service_pb';

import {Graph} from './execution/graph';
import {OpentronsActionsHandler} from './execution/opentrons_actions_handler';
import {OpentronsSimulatingExecutor} from './execution/opentrons_simulating_executor';
import {
  ListeningExecutor,
  Profile,
  ProfilingActionsHandler,
} from './execution/profiling_actions_handler';
import {OpentronsFlexRobot} from './execution/robots';
import {settle} from './execution/settling';

export interface Args {
  graph: Graph;
  robots: OpentronsFlexRobot[];
}

export interface State {
  currentUser: Future<GetCurrentUserResponse>;
  error: string | undefined;
  profile: Profile | undefined;
  robot: OpentronsFlexRobot;
  script: string | undefined;
  view: 'code' | 'labware';
}

export class SimulationController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly graph: Graph;
  private readonly robots: OpentronsFlexRobot[];

  constructor(response: Response<SimulationController>) {
    super(response);
    this.graph = response.args.graph;
    this.robots = response.args.robots;

    this.state.currentUser.then(() => this.updateScript());
  }

  async changeView(e: CorgiEvent<typeof CHANGED>): Promise<void> {
    const view = e.detail.value as State['view'];
    await this.updateState({...this.state, view});
  }

  async selectRobot(e: CorgiEvent<typeof CHANGED>): Promise<void> {
    await this.updateState({
      ...this.state,
      robot: this.robots[Number(e.detail.value)],
    });
    this.updateScript();
  }

  private updateScript(): void {
    getScript(
      this.state.currentUser.value().userEmail,
      this.graph,
      this.state.robot,
    )
      .then(({profile, script}) =>
        this.updateState({
          ...this.state,
          error: undefined,
          profile,
          script,
        }),
      )
      .catch(e => {
        if (e instanceof Error) {
          this.updateState({
            ...this.state,
            error: `${e.message}\n\n${e.stack}`,
          });
        } else {
          this.updateState({...this.state, error: String(e)});
        }
      });
  }
}

async function getScript(
  author: string,
  graph: Graph,
  robot: OpentronsFlexRobot,
): Promise<{
  script: string;
  profile: Profile;
}> {
  // First get a naive profile so we can help the OAH work
  const naiveProfiler = ProfilingActionsHandler.makeNaiveProfiler(robot);
  await settle(await graph.clone(), naiveProfiler, /* sandbox= */ true);
  const naiveProfile = naiveProfiler.getProfile();

  // Then rerun with a new profiler so we can record what actually happened
  const executor = new OpentronsSimulatingExecutor(
    graph.getName(),
    author,
    robot.name,
  );
  const listener = new ListeningExecutor(executor);
  const handler = new OpentronsActionsHandler(
    listener,
    naiveProfile,
    robot,
    /* sandbox= */ false,
  );
  const wiseProfiler = new ProfilingActionsHandler(handler, listener);
  await settle(await graph.clone(), wiseProfiler);
  return {
    profile: wiseProfiler.getProfile(),
    script: executor.getScript(),
  };
}
